import type { PropsWithChildren } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useNavigate } from '@xing-com/crate-core-router';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { useSearchParameter } from '@xing-com/crate-hooks-use-search-parameter';
import { useBannerContext } from '@xing-com/crate-jobs-components-banner';
import { useLazyGetUserRecentCvs } from '@xing-com/crate-jobs-domain-apply-hooks';
import type { ApplicationType } from '@xing-com/crate-jobs-helpers';
import {
  useGetJobApplicationType,
  useGetLazyNeffiScore,
} from '@xing-com/crate-jobs-hooks';
import { IconButterflyMono } from '@xing-com/icons';

import { useRedirectToLoginWithJobDestUrl } from './use-redirect-to-login-with-job-dest-url';
import type { ApplyTrackingProps } from './use-track-apply-intention';
import { useTrackApplyIntention } from './use-track-apply-intention';

const QUICK_APPLY_NEFFI_SCORE_THRESHOLD = 100;

type InstantApplyButtonState = {
  icon: typeof IconButterflyMono;
  to: undefined;
  onClick: (trackingProps?: ApplyTrackingProps) => void;
  loading: boolean;
};

type ApplyButtonStateProviderProps = {
  applicationType?: ApplicationType;
  jobId: string;
  jobSlug: string;
};

const ApplyButtonStateContext = createContext<InstantApplyButtonState>({
  icon: IconButterflyMono,
  to: undefined,
  onClick: () => ({}),
  loading: false,
});

export const ApplyButtonStateProvider: React.FC<
  PropsWithChildren<ApplyButtonStateProviderProps>
> = ({ applicationType = 'JobXingApplication', children, jobId, jobSlug }) => {
  const isXingApplyFlow = applicationType === 'JobXingApplication';
  const { isLoggedIn } = useLoginState();
  const navigate = useNavigate();
  const {
    getJobApplicationType,
    isLoading: isLoadingApplicationType,
    data,
    error,
  } = useGetJobApplicationType();
  const {
    getLazyNeffiScore,
    normalizedNeffiScore,
    isLoading: isLoadingNeffi,
  } = useGetLazyNeffiScore();
  const {
    getUserRecentCvs,
    userRecentCvs,
    isLoading: isLoadingCvs,
  } = useLazyGetUserRecentCvs();
  const { showErrorBanner } = useBannerContext();
  const redirectToLogin = useRedirectToLoginWithJobDestUrl();
  const { getSearchParameter } = useSearchParameter();
  const trackApplyIntention = useTrackApplyIntention({
    applicationType,
    jobId,
  });
  const [isApplyClickInProgress, setIsApplyClickInProgress] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const resetAndShowError = useCallback(
    (errorCopy: string) => {
      showErrorBanner(errorCopy);
      setIsApplyClickInProgress(false);
    },
    [showErrorBanner]
  );

  useEffect(() => {
    if (isLoadingApplicationType) {
      return;
    }

    if (isApplyClickInProgress) {
      if (error) {
        resetAndShowError('JOBS_ERROR_SOMETHING_WENT_WRONG');
        return;
      }
      const {
        isVisibleJob,
        applicationType: apiApplicationType,
        isLoApplyEnabled,
      } = data;

      if (!isVisibleJob) {
        resetAndShowError('JOBS_JOB_DETAILS_XING_APPLY_DEACTIVATED_ERROR');
        return;
      }
      if (applicationType !== apiApplicationType) {
        resetAndShowError('JOBS_JOB_DETAILS_XING_APPLY_TYPE_ERROR');
        return;
      }

      if (isLoadingNeffi || isLoadingCvs || isRedirecting) {
        return;
      }

      setIsRedirecting(true);

      const fixedParams = new URLSearchParams();
      const ijt = getSearchParameter('ijt');
      if (ijt) {
        fixedParams.set('ijt', ijt);
      }
      const applyPath = `${isXingApplyFlow ? '/jobs/apply' : '/jobs/custom-apply'}/${jobSlug}?${fixedParams.toString()}`;

      if (isLoggedIn) {
        if (
          isXingApplyFlow &&
          (userRecentCvs.length > 0 ||
            normalizedNeffiScore >= QUICK_APPLY_NEFFI_SCORE_THRESHOLD)
        ) {
          navigate(`/jobs/quick-apply/${jobSlug}?${fixedParams.toString()}`);
        } else {
          navigate(applyPath);
        }
      } else if (isLoApplyEnabled) {
        navigate(applyPath);
      } else {
        redirectToLogin();
      }
    }
  }, [
    data,
    error,
    isLoggedIn,
    isLoadingApplicationType,
    isRedirecting,
    navigate,
    getSearchParameter,
    jobSlug,
    isLoadingNeffi,
    isLoadingCvs,
    normalizedNeffiScore,
    redirectToLogin,
    isApplyClickInProgress,
    resetAndShowError,
    applicationType,
    isXingApplyFlow,
    userRecentCvs.length,
  ]);

  const onClick = useCallback(
    (trackingProps?: Record<string, unknown>) => {
      trackApplyIntention(trackingProps);
      setIsApplyClickInProgress(true);
      getJobApplicationType({
        variables: { slug: jobSlug },
      });

      if (isLoggedIn && isXingApplyFlow) {
        getLazyNeffiScore();
        getUserRecentCvs();
      }
    },
    [
      getJobApplicationType,
      getLazyNeffiScore,
      getUserRecentCvs,
      isLoggedIn,
      isXingApplyFlow,
      jobSlug,
      trackApplyIntention,
    ]
  );

  const applyButtonState = useMemo(() => {
    return {
      icon: IconButterflyMono,
      to: undefined, // overwrites default LO fencing redirection
      onClick,
      loading:
        isLoadingApplicationType ||
        isLoadingNeffi ||
        isLoadingCvs ||
        isRedirecting,
    };
  }, [
    isLoadingApplicationType,
    isLoadingCvs,
    isLoadingNeffi,
    isRedirecting,
    onClick,
  ]);

  return (
    <ApplyButtonStateContext.Provider value={applyButtonState}>
      {children}
    </ApplyButtonStateContext.Provider>
  );
};

export const useApplyButtonStateProps = (): InstantApplyButtonState => {
  return useContext(ApplyButtonStateContext);
};
